import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SearchTabSearchBar.scss';
import { MagnifyingGlassGold } from '@/resources/assets/images';
import Loader, { Status } from '@/components/Loader/Loader';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import type { BusinessQuery, Search } from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { getCategorySuggestions } from '@/services/categoryService';
import { debounce } from 'lodash';

interface Props {
	includeSelected: boolean;
	onFilterChange: (query: BusinessQuery) => void;
	onGetBusiness: (query: BusinessQuery) => void;
}

const SearchTabSearchBar = ({
	includeSelected,
	onFilterChange,
	onGetBusiness
}: Props) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [searchBarValue, setSearchBarValue] = useState<string>('');
	const [hideSearchBarDropdown, setHideSearchBarDropdown] =
		useState<boolean>(false);
	const [initialSearchBarDropdownRender, setInitialSearchBarDropdownRender] =
		useState<boolean>(false);
	const [categorySuggestions, setCategorySuggestions] = useState<string[]>(
		[]
	);
	const [isLoadingSuggestions, setIsLoadingSuggestions] =
		useState<boolean>(false);

	const dispatch = useDispatch();

	const searchBarDivRef = useRef<HTMLDivElement>(null);
	const searchBarDropdownRef = useRef<HTMLDivElement>(null);

	// Create a stable debounced function
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const fetchSuggestionsDebounced = useCallback(
		debounce((prefix: string, searches: Search[] | undefined) => {
			setIsLoadingSuggestions(true);
			getCategorySuggestions(prefix)
				.then(response => {
					// Check if the request was successful and the server response indicates OK
					if (
						response.status === 200 &&
						response.data &&
						response.data.code === 'OK'
					) {
						const actualSuggestions = response.data.data;

						// Filter out categories that are already in the search
						const filteredSuggestions = actualSuggestions.filter(
							// Use actual array
							name =>
								!searches?.some(
									(filter: Search) =>
										filter.value.toLowerCase() ===
										name.toLowerCase()
								)
						);

						setCategorySuggestions(filteredSuggestions);
					} else {
						setCategorySuggestions([]);
					}
				})
				.catch(() => {
					setCategorySuggestions([]);
				})
				.finally(() => {
					setIsLoadingSuggestions(false);
				});
		}, 300),
		[setCategorySuggestions, setIsLoadingSuggestions]
	);

	// Function to fetch category suggestions
	const debouncedFetchSuggestions = useCallback(
		(prefix: string) => {
			fetchSuggestionsDebounced(prefix, query?.searches);
		},
		[fetchSuggestionsDebounced, query?.searches]
	);

	const searchBarOnChangeHandler = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		if (hideSearchBarDropdown) {
			setHideSearchBarDropdown(false);
		}

		const newValue = e.target.value;
		setSearchBarValue(newValue);

		// Fetch suggestions when the user types
		debouncedFetchSuggestions(newValue);
	};

	const searchBarOnFocusHandler = () => {
		if (!initialSearchBarDropdownRender) {
			setInitialSearchBarDropdownRender(true);
			// First time focus - show popular categories
			debouncedFetchSuggestions('');
		} else {
			// Subsequent focus - use existing value or empty for popular
			debouncedFetchSuggestions(searchBarValue);
		}

		setHideSearchBarDropdown(false);
	};

	const addSearchTermToQuery = (searchTerm: string) => {
		const searchesArray: NonNullable<BusinessQuery['searches']> =
			query.searches || [];

		const lastestIndex = query.searches?.at(-1)?.index || 0;
		const index = query.searches?.length ? lastestIndex + 1 : 0;

		searchesArray.push({
			value: searchTerm,
			type: includeSelected ? 0 : 1,
			index
		});

		const excluded: Array<(typeof searchesArray)[0]['value']> = [];
		const included: Array<(typeof searchesArray)[0]['value']> = [];

		searchesArray.forEach(search => {
			if (search.type === 1) {
				return excluded.push(search.value);
			}

			return included.push(search.value);
		});

		const newQuery = {
			...query,
			skip: 0,
			searches: searchesArray,
			includes: included,
			excludes: excluded,
			user
		};

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User made a search',
			properties: {
				query: newQuery,
				querySearch: searchesArray,
				excludedFromSearch: excluded,
				includedInSearch: included
			}
		});

		onFilterChange(newQuery);

		onGetBusiness(newQuery);
	};

	const handleClickSuggestion = (suggestion: string) => {
		addSearchTermToQuery(suggestion);
		setHideSearchBarDropdown(true);
		setSearchBarValue('');
	};

	const searchBarOnKeyDownHandler = (
		e: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (e.key === 'Enter') {
			setHideSearchBarDropdown(true);
			// Use the first suggestion if available, otherwise use the search bar value
			addSearchTermToQuery(
				searchBarValue.length === 0 && categorySuggestions.length > 0
					? categorySuggestions[0]
					: searchBarValue
			);
			setSearchBarValue('');
		}
	};

	const closeSuggestionsDropdown = useCallback(
		(e: MouseEvent) => {
			const target = e.target as Node;
			if (
				!searchBarDivRef?.current?.contains(target) &&
				!searchBarDropdownRef?.current?.contains(target) &&
				!hideSearchBarDropdown
			) {
				setHideSearchBarDropdown(true);
			}
		},
		[hideSearchBarDropdown]
	);

	useEffect(() => {
		window.addEventListener('click', closeSuggestionsDropdown);

		return () =>
			window.removeEventListener('click', closeSuggestionsDropdown);
	}, [closeSuggestionsDropdown]);

	return (
		<div
			ref={searchBarDivRef}
			className="search-tab-filters-list-item-dropdown-search-bar"
		>
			<MagnifyingGlassGold className="search-tab-filters-list-item-dropdown-search-bar-icon" />
			<input
				className="search-tab-filters-list-item-dropdown-search-bar-input"
				type="text"
				placeholder="Search"
				value={searchBarValue}
				onChange={searchBarOnChangeHandler}
				onFocus={searchBarOnFocusHandler}
				onKeyDown={searchBarOnKeyDownHandler}
			/>
			{!hideSearchBarDropdown && initialSearchBarDropdownRender && (
				<div
					ref={searchBarDropdownRef}
					className="search-tab-filters-list-item-dropdown-search-bar-dropdown"
				>
					{searchBarValue.length > 0 && (
						<div
							className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item first"
							onClick={() =>
								handleClickSuggestion(searchBarValue)
							}
							role="button"
							tabIndex={0}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									handleClickSuggestion(searchBarValue);
								}
							}}
						>
							Search
							<span>{searchBarValue}</span>
						</div>
					)}
					{isLoadingSuggestions ? (
						<div className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item loading">
							<Loader
								className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item-loader"
								status={Status.LOADING}
							/>
						</div>
					) : (
						categorySuggestions.map((category: string) => (
							<div
								key={category}
								className="search-tab-filters-list-item-dropdown-search-bar-dropdown-item"
								onClick={() => handleClickSuggestion(category)}
								role="button"
								tabIndex={0}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										handleClickSuggestion(category);
									}
								}}
							>
								{category}
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
};

export default SearchTabSearchBar;
